import { useCallback } from 'react';
import { toast } from 'sonner';

import { AssetActionHandlers } from '@/sections/asset/types';
import { Asset } from '@/types';

import { useAssetScanHandlers } from './useAssetScanHandlers';
import { useAssetStatusHandlers } from './useAssetStatusHandlers';

/**
 * Main coordinator hook for asset action handlers
 * Composes scan and status handlers with share functionality
 *
 * @returns Combined action handlers interface
 */
export const useAssetActionHandlers = (): AssetActionHandlers => {
  const { handleScan } = useAssetScanHandlers();
  const { handleUpdateStatus } = useAssetStatusHandlers();

  /**
   * Shares asset URLs by copying them to clipboard
   * Generates deep links with drawer state
   *
   * @param assets - Assets to share
   */
  const handleShare = useCallback((assets: Asset[]) => {
    const urls = assets.map(asset => {
      // Extract base path up to userId (e.g., "/encoded-user-id")
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const basePath = pathParts[0] ? `/${pathParts[0]}` : '';

      const params = new URLSearchParams();
      params.append('assetDrawerKey', asset.key);
      params.append('drawerOrder', JSON.stringify(['asset']));
      return `${window.location.origin}${basePath}/assets?${params.toString()}`;
    });

    navigator.clipboard.writeText(urls.join('\n'));
    toast.success(
      `Copied ${urls.length} asset URL${urls.length > 1 ? 's' : ''} to clipboard`
    );
  }, []);

  return {
    handleScan,
    handleUpdateStatus,
    handleShare,
  };
};
